import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { UserProfile, AuthTokens } from '../types/api/auth';
import { useAuth } from '../hooks/api/useAuth';
import type { ApiErrorResponse } from '../types/api/common';

/**
 * Auth Context State Interface
 */
interface AuthContextState {
  // User and authentication state
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: ApiErrorResponse | null;

  // Authentication actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginAdmin: (email: string, password: string) => Promise<void>;
  loginMerchant: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  logoutAdmin: () => Promise<void>;
  logoutMerchant: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  updateProfile: (profileData: any) => Promise<void>;
  changePassword: (passwordData: any) => Promise<void>;

  // Utility functions
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  clearError: () => void;

  // Loading states for specific operations
  isLoggingIn: boolean;
  isRegistering: boolean;
  isUpdatingProfile: boolean;
  isChangingPassword: boolean;
}

/**
 * Auth Context Props Interface
 */
interface AuthContextProps {
  children: ReactNode;
}

/**
 * Authentication Context for managing user authentication state
 */
const AuthContext = createContext<AuthContextState | undefined>(undefined);

/**
 * Authentication Context Provider Component
 * 
 * Features:
 * - User authentication state management
 * - Token management and refresh
 * - Role-based access control
 * - Cross-tab synchronization
 * - Persistent login sessions
 * - Error handling
 */
export function AuthProvider({ children }: AuthContextProps) {
  const authHook = useAuth();
  const [sessionTimeout, setSessionTimeout] = useState<number | null>(null);

  /**
   * Set up session timeout monitoring
   */
  useEffect(() => {
    if (authHook.isAuthenticated && authHook.tokens) {
      // Calculate session timeout
      const expiresIn = authHook.tokens.expiresIn * 1000; // Convert to milliseconds
      const timeout = setTimeout(() => {
        console.warn('Session is about to expire');
        // Could show a warning dialog here
        
        // Auto-refresh tokens if possible
        authHook.refreshTokens().catch(() => {
          console.error('Failed to refresh session, logging out');
          authHook.logout();
        });
      }, expiresIn - 5 * 60 * 1000); // 5 minutes before expiry

      setSessionTimeout(timeout);

      return () => {
        if (sessionTimeout) {
          clearTimeout(sessionTimeout);
        }
      };
    }
  }, [authHook.isAuthenticated, authHook.tokens]);

  /**
   * Handle visibility change for session management
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && authHook.isAuthenticated) {
        // Page became visible, check if tokens need refresh
        if (authHook.tokens) {
          // Check if token is close to expiry
          const now = Date.now();
          const tokenExpiry = now + (authHook.tokens.expiresIn * 1000);
          const timeUntilExpiry = tokenExpiry - now;
          
          if (timeUntilExpiry < 10 * 60 * 1000) { // Less than 10 minutes
            authHook.refreshTokens().catch((error) => {
              console.error('Failed to refresh tokens on visibility change:', error);
            });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authHook.isAuthenticated, authHook.tokens, authHook.refreshTokens]);

  /**
   * Enhanced login function with error handling
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      await authHook.login({ usernameOrEmail: email, password });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced admin login function with error handling
   */
  const loginAdmin = async (email: string, password: string): Promise<void> => {
    try {
      await authHook.loginAdmin({ usernameOrEmail: email, password });
    } catch (error) {
      console.error('Admin login failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced merchant login function with error handling
   */
  const loginMerchant = async (email: string, password: string): Promise<void> => {
    try {
      await authHook.loginMerchant({ usernameOrEmail: email, password });
    } catch (error) {
      console.error('Merchant login failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced register function
   */
  const register = async (userData: any): Promise<void> => {
    try {
      await authHook.register(userData);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced logout function with cleanup (legacy - for backward compatibility)
   * @deprecated Use logoutAdmin or logoutMerchant instead
   */
  const logout = async (): Promise<void> => {
    try {
      // Clear session timeout
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        setSessionTimeout(null);
      }

      await authHook.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced admin logout function with cleanup
   */
  const logoutAdmin = async (): Promise<void> => {
    try {
      // Clear session timeout
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        setSessionTimeout(null);
      }

      await authHook.logoutAdmin();
    } catch (error) {
      console.error('Admin logout failed:', error);
      throw error;
    }
  };

  /**
   * Enhanced merchant logout function with cleanup
   */
  const logoutMerchant = async (): Promise<void> => {
    try {
      // Clear session timeout
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        setSessionTimeout(null);
      }

      await authHook.logoutMerchant();
    } catch (error) {
      console.error('Merchant logout failed:', error);
      throw error;
    }
  };

  /**
   * Context value with all authentication state and actions
   */
  const contextValue: AuthContextState = {
    // State
    user: authHook.user,
    tokens: authHook.tokens,
    isAuthenticated: authHook.isAuthenticated,
    isLoading: authHook.isLoading,
    error: authHook.error,

    // Actions
    login,
    loginAdmin,
    loginMerchant,
    register,
    logout,
    logoutAdmin,
    logoutMerchant,
    refreshTokens: authHook.refreshTokens,
    updateProfile: authHook.updateProfile,
    changePassword: authHook.changePassword,

    // Utility functions
    hasRole: authHook.hasRole,
    hasAnyRole: authHook.hasAnyRole,
    clearError: authHook.clearError,

    // Loading states
    isLoggingIn: authHook.isLoggingIn,
    isRegistering: authHook.isRegistering,
    isUpdatingProfile: authHook.isUpdatingProfile,
    isChangingPassword: authHook.isChangingPassword,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 */
export function useAuthContext(): AuthContextState {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  
  return context;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuthContext();
  return isAuthenticated;
}

/**
 * Hook to get current user
 */
export function useCurrentUser(): UserProfile | null {
  const { user } = useAuthContext();
  return user;
}

/**
 * Hook for role-based access control
 */
export function useRoleCheck(): {
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: boolean;
  isMerchant: boolean;
  isRestaurantOwner: boolean;
  isDeliveryDriver: boolean;
} {
  const { hasRole, hasAnyRole } = useAuthContext();

  return {
    hasRole,
    hasAnyRole,
    isAdmin: hasRole('ADMIN'),
    isMerchant: hasRole('MERCHANT'),
    isRestaurantOwner: hasRole('RESTAURANT_OWNER'),
    isDeliveryDriver: hasRole('DELIVERY_DRIVER'),
  };
}

/**
 * Hook for authentication loading states
 */
export function useAuthLoading(): {
  isLoading: boolean;
  isLoggingIn: boolean;
  isRegistering: boolean;
  isUpdatingProfile: boolean;
  isChangingPassword: boolean;
  isAnyLoading: boolean;
} {
  const {
    isLoading,
    isLoggingIn,
    isRegistering,
    isUpdatingProfile,
    isChangingPassword,
  } = useAuthContext();

  const isAnyLoading = isLoading || isLoggingIn || isRegistering || isUpdatingProfile || isChangingPassword;

  return {
    isLoading,
    isLoggingIn,
    isRegistering,
    isUpdatingProfile,
    isChangingPassword,
    isAnyLoading,
  };
}

/**
 * Hook for authentication errors
 */
export function useAuthError(): {
  error: ApiErrorResponse | null;
  hasError: boolean;
  clearError: () => void;
  isUnauthorized: boolean;
  isForbidden: boolean;
  isValidationError: boolean;
} {
  const { error, clearError } = useAuthContext();

  return {
    error,
    hasError: !!error,
    clearError,
    isUnauthorized: error?.error?.type === 'AUTHENTICATION',
    isForbidden: error?.error?.type === 'AUTHORIZATION',
    isValidationError: error?.error?.type === 'VALIDATION',
  };
}

/**
 * Higher-order component for protected routes
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles?: string[]
) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading, hasAnyRole } = useAuthContext();

    if (isLoading) {
      return <div>Loading...</div>; // Could be replaced with a proper loading component
    }

    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>; // Could redirect to login
    }

    if (requiredRoles && !hasAnyRole(requiredRoles)) {
      return <div>You don't have permission to access this page.</div>;
    }

    return <Component {...props} />;
  };
}

/**
 * Component for conditional rendering based on authentication
 */
interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  requireAuth?: boolean;
  requiredRoles?: string[];
}

export function AuthGuard({
  children,
  fallback = null,
  requireAuth = true,
  requiredRoles,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasAnyRole } = useAuthContext();

  if (isLoading) {
    return <div>Loading...</div>; // Could be replaced with a proper loading component
  }

  if (requireAuth && !isAuthenticated) {
    return <>{fallback}</>;
  }

  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
